// H-19: two candidates side by side, aligned by capability.
//
// Two, never more, and that is a deliberate limit rather than a layout one. A
// wider grid invites ranking by eye down a column of levels, which is exactly
// the "a score that effectively decides" problem L-3 exists to prevent. Two
// candidates is a judgement a person makes between two people.
//
// Each box carries the level AND the analysis behind it, so the comparison is
// of evidence rather than of labels. They read as the candidate's Capabilities
// tab does, so nothing has to be learned twice.
//
// What it must NOT do, and what is deliberately absent: there is no total, no
// composite score and no winner. The choice stays a person's (L-3).

import React from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { useHiringCompare } from '../../../../queries/useHiring';
import {
  ActionButton,
  BODY,
  BRANDY,
  FONT,
  LABEL,
  LINE,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3,
  WELL
} from '../capability-setup/designSystem';
import { GRID_LINE, IntegrityCaption, LevelPill, RoleplayContext } from './hiringParts';

const CompareDialog: React.FC<{ roundId: string; ids: string[]; attempts: number; onClose: () => void }> = ({
  roundId,
  ids,
  attempts,
  onClose
}) => {
  const compare = useHiringCompare(roundId, ids);
  const candidates = compare.data?.candidates || [];
  const columns = `repeat(${candidates.length || 2}, minmax(0, 1fr))`;
  return (
    <Dialog
      open={ids.length === 2}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '10px' } }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>
              Compare Candidates
            </Typography>
            <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3, mt: '2px' }}>
              Side by side view to allow you to conclude your preferred candidate.
            </Typography>
          </Box>
          <ActionButton onClick={onClose}>Close</ActionButton>
        </Box>

        {compare.isLoading ? (
          <Box sx={{ ...fadeInSx }}>
            <Bone height={320} />
          </Box>
        ) : (
          <Box sx={{ maxHeight: '72vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <IntegrityCaption />
            {/* Stays in view, so a long list never loses whose column is whose. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: columns,
                gap: '12px',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                py: '4px',
                bgcolor: SURFACE
              }}
            >
              {candidates.map((candidate) => (
                <Typography
                  key={candidate.id}
                  sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: TEXT, textAlign: 'center' }}
                >
                  {candidate.name}
                </Typography>
              ))}
            </Box>

            {(compare.data?.capabilities || []).map((capability) => (
              <Box key={capability.capabilityId} sx={{ display: 'grid', gridTemplateColumns: columns, gap: '12px' }}>
                {candidates.map((candidate) => {
                  const row = candidate.capabilities.find((c) => c.capabilityId === capability.capabilityId);
                  return (
                    <Box
                      key={candidate.id}
                      sx={{
                        minWidth: 0,
                        p: '14px 16px',
                        borderRadius: '4px',
                        bgcolor: WELL,
                        border: `1px solid ${LINE}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        '& > * + * + *': { borderTop: GRID_LINE, pt: '12px' }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          pb: '10px',
                          borderBottom: '1px solid rgba(247, 148, 32, 0.45)'
                        }}
                      >
                        <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: BRANDY, flex: 1 }}>
                          {capability.capabilityName}
                        </Typography>
                        <LevelPill level={row?.level ?? null} label={row?.label} />
                      </Box>
                      {row && row.attemptsUsed > 0 && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', mb: '6px' }}>
                            <Typography sx={{ ...LABEL, color: TEXT, flex: 1, minWidth: 'fit-content' }}>
                              Roleplay Analysis
                            </Typography>
                            <RoleplayContext capability={row} attempts={attempts} />
                          </Box>
                          {row.analysis && <Typography sx={{ ...BODY, color: TEXT_2 }}>{row.analysis}</Typography>}
                        </Box>
                      )}
                      {row?.cvAnalysis && (
                        <Box>
                          <Typography sx={{ ...LABEL, color: TEXT, mb: '6px' }}>CV Analysis</Typography>
                          <Typography sx={{ ...BODY, color: TEXT_2 }}>{row.cvAnalysis}</Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default CompareDialog;
